import { useMemo } from 'react';
import { getBranchHealth } from '../../domain/branchHealth';
import type { PaymentMethodRow } from '../../../sales/public-api';
import {
  buildPaymentMethodNames,
  DEFAULT_PAYMENT_METHOD_LABELS,
} from '../reportsScreen.utils';
import type { useReportsDashboardFetch } from './useReportsDashboardFetch';

type FetchState = Pick<
  ReturnType<typeof useReportsDashboardFetch>,
  | 'cashSummary'
  | 'todaySalesSnapshot'
  | 'periodSalesSnapshot'
  | 'inventoryDashboard'
  | 'paymentMethods'
>;

type Params = {
  fetch: FetchState;
  customerModuleEnabled: boolean;
  mercadopagoQrMethodAllowed: boolean;
};

export function useReportsDashboardDerived({
  fetch,
  customerModuleEnabled,
  mercadopagoQrMethodAllowed,
}: Params) {
  const paymentMethodNames = useMemo(() => {
    const methodsForLabels = fetch.paymentMethods.filter((m: PaymentMethodRow) => {
      if (m.id === 'fiado' && !customerModuleEnabled) return false;
      if (m.id !== 'mercadopago_qr') return true;
      return mercadopagoQrMethodAllowed;
    });
    return {
      ...DEFAULT_PAYMENT_METHOD_LABELS,
      ...buildPaymentMethodNames(methodsForLabels),
    };
  }, [fetch.paymentMethods, customerModuleEnabled, mercadopagoQrMethodAllowed]);

  const branchHealth = useMemo(
    () =>
      getBranchHealth({
        syncFailed: 0,
        syncPending: 0,
        stockNegative: fetch.inventoryDashboard?.negative ?? 0,
        stockExpiringSoon: fetch.inventoryDashboard?.expiringSoon ?? 0,
        stockBelowMin: fetch.inventoryDashboard?.belowMin ?? 0,
        pendingPayments: fetch.todaySalesSnapshot?.pendingPayments ?? 0,
      }),
    [
      fetch.inventoryDashboard?.negative,
      fetch.inventoryDashboard?.expiringSoon,
      fetch.inventoryDashboard?.belowMin,
      fetch.todaySalesSnapshot?.pendingPayments,
    ]
  );

  const salesFiscalCount = fetch.periodSalesSnapshot?.fiscalSalesCount ?? 0;
  const salesFiscalTotal = fetch.periodSalesSnapshot?.fiscalSalesTotal ?? 0;
  const salesNonFiscalCount = fetch.periodSalesSnapshot?.nonFiscalSalesCount ?? 0;
  const salesNonFiscalTotal = fetch.periodSalesSnapshot?.nonFiscalSalesTotal ?? 0;

  const todaySalesFiscalCount = fetch.todaySalesSnapshot?.fiscalSalesCount ?? 0;
  const todaySalesFiscalTotal = fetch.todaySalesSnapshot?.fiscalSalesTotal ?? 0;
  const todaySalesNonFiscalCount = fetch.todaySalesSnapshot?.nonFiscalSalesCount ?? 0;
  const todaySalesNonFiscalTotal = fetch.todaySalesSnapshot?.nonFiscalSalesTotal ?? 0;

  return {
    paymentMethodNames,
    branchHealth,
    salesFiscalCount,
    salesFiscalTotal,
    salesNonFiscalCount,
    salesNonFiscalTotal,
    todaySalesFiscalCount,
    todaySalesFiscalTotal,
    todaySalesNonFiscalCount,
    todaySalesNonFiscalTotal,
  };
}
